/**
 * USMSTF (US Multi-Society Task Force on Colorectal Cancer) 2020 post-polypectomy surveillance-
 * interval rules engine — `BACKLOG.md` M5.
 *
 * **UNREVIEWED — this entire interval table, not just one field.** `BACKLOG.md`'s own M5 entry states
 * this explicitly: "a USMSTF 2020 surveillance-interval rules engine (the actual thresholds need
 * re-verification — PubMed fetch was blocked during research)." The category boundaries and interval
 * values below are recalled from general familiarity with the 2020 USMSTF guidance (Gupta et al.), not
 * transcribed from a fetched primary source this session — the M5-M11 section's own methodology
 * caveat (`BACKLOG.md`, immediately above the M5 entry) names PubMed specifically as one of the
 * sources blocked during research.
 *
 * Per `claude-kit/rules/invariants.md`'s "never hard-code an unverified clinical threshold as if it
 * were validated fact": **the engine — the rule-matching logic, the category shape, the "most severe
 * finding wins" precedence — is built for real and is the actual reusable asset**, matching
 * `dermatology/phototherapy.ts`'s own precedent (`NBUVB_PROTOCOL`, marked UNREVIEWED, values ported
 * without independent verification, engine still shipped). What must not happen: presenting
 * `recommendSurveillanceInterval`'s return value to a clinician as validated guidance, or wiring it
 * into an actual scheduling/recall decision, until every category boundary and interval below is
 * checked against the actual USMSTF 2020 publication and that citation is recorded here, replacing
 * this comment.
 *
 * SHAPE OF THE RULE, computed from one report's full polyp list plus a same-encounter finding of any
 * sessile-serrated polyp ≥ 10mm or piecemeal resection of a large polyp:
 *
 *   - No polyps, or only diminutive (< 10mm) hyperplastic polyps in the rectum/sigmoid — 10 years
 *     (same as an average-risk screening interval; not a surveillance interval at all).
 *   - 1-2 tubular adenomas, both < 10mm — 7-10 years.
 *   - 3-4 tubular adenomas, all < 10mm — 3-5 years.
 *   - 5-10 tubular adenomas, all < 10mm — 3 years.
 *   - More than 10 adenomas in one exam — 1 year.
 *   - Any adenoma ≥ 10mm — 3 years.
 *   - Any adenoma with villous histology or high-grade dysplasia — 3 years.
 *   - 1-2 sessile serrated lesions, both < 10mm, no dysplasia — 5-10 years.
 *   - 3-4 sessile serrated lesions, all < 10mm — 3-5 years.
 *   - 5-10 sessile serrated lesions, all < 10mm — 3 years (added 2026-08-24 — see the correction
 *     note immediately below the rule list; this tier was missing from an earlier version of both
 *     this comment and the implementation, collapsed into the 3-4-tier's 3-5-year range).
 *   - Sessile serrated lesion ≥ 10mm, or with dysplasia, or a traditional serrated adenoma — 3 years.
 *   - A hyperplastic polyp that is ≥ 10mm or outside the rectum/sigmoid, with no higher-risk finding
 *     on the same report — 3-5 years (added 2026-08-26, see the E8 correction note below; distinct
 *     from a true sessile serrated lesion/TSA ≥ 10mm, which stays in the 3-year bucket above).
 *   - Piecemeal resection of a polyp ≥ 20mm — 6 months (a post-resection surveillance exam, not a
 *     standard-interval surveillance recommendation).
 *
 * **Correction, 2026-08-24, confirmed against USMSTF 2020 (Gupta et al., Gastroenterology/GIE 2020,
 * Table 4/5):** an earlier version of this module collapsed "3-4 polyps" and "5-10 polyps" into one
 * 3-5-year bucket for both adenomas and sessile serrated lesions — the real guideline gives 5-10 (of
 * either kind) their own flat 3-year interval, distinct from 3-4's 3-5-year range. Fixed here, both
 * for adenomas (`high_adenoma_count`, new) and for serrated lesions (`high_serrated_count`, new).
 *
 * **Correction, 2026-08-26, per `emr-po` BACKLOG.md's E8 review (same research pass as the 2026-08-24
 * correction above, action item not applied until now):** a hyperplastic polyp that is not diminutive-
 * and-rectosigmoid was falling through this function entirely to `no_polyps_or_diminutive_hyperplastic`
 * (10-year average-risk) — `isSerratedHistology` deliberately excludes `hyperplastic_polyp` (correct,
 * for ADR-counting), so this case never reached the high-risk serrated check either. Fixed with a new
 * `non_diminutive_hyperplastic_polyp` category (3-5 years), checked at "moderate" priority so it can't
 * be silently outranked by a less-conservative low-adenoma/low-serrated finding on the same report.
 * **A separate, NOT-yet-fixed gap surfaced while fixing this**, tracked in `computeSurveillanceCategory`'s
 * own final comment rather than here: an `adenocarcinoma`/`normal_mucosa`/`other` histology has no
 * matching category and currently also resolves to the 10-year bucket — `adenocarcinoma` doing so is a
 * real concern, since a cancer finding arguably shouldn't route through a "surveillance interval"
 * category at all.
 *
 * `computeSurveillanceCategory` evaluates every polyp on a report and returns the single most
 * conservative (shortest-interval) matching category — never averages or picks the first match — the
 * same "most severe finding governs" precedence the CPT count-banding module
 * (`destructionCharges.ts`) and recall engine (`recallRules.ts`) both already use for their own
 * shortest-safe-interval reasoning, applied here to a genuinely different clinical question.
 */

import {
  isAdenomaHistology,
  isSerratedHistology,
  type DysplasiaGrade,
  type HistologyType,
} from './polypFindings.ts';

/** A category, ordered from most conservative (shortest recommended interval) to least. Order is
 * significant — `computeSurveillanceCategory` walks this list and returns the first category whose
 * `matches` predicate is satisfied by at least one qualifying subset of the report's polyps. */
export const SURVEILLANCE_CATEGORIES = [
  'piecemeal_large_polyp_resection',
  'more_than_10_adenomas',
  'high_risk_adenoma_or_serrated',
  'high_adenoma_count',
  'high_serrated_count',
  'moderate_adenoma_count',
  'moderate_serrated_count',
  'non_diminutive_hyperplastic_polyp',
  'low_adenoma_count',
  'low_serrated_count',
  'no_polyps_or_diminutive_hyperplastic',
] as const;
export type SurveillanceCategory = (typeof SURVEILLANCE_CATEGORIES)[number];

export interface SurveillanceIntervalRange {
  readonly minYears: number;
  readonly maxYears: number;
}

/** UNREVIEWED — see this module's header. */
const CATEGORY_INTERVALS: Readonly<Record<SurveillanceCategory, SurveillanceIntervalRange>> = {
  piecemeal_large_polyp_resection: { minYears: 0.5, maxYears: 0.5 },
  more_than_10_adenomas: { minYears: 1, maxYears: 1 },
  high_risk_adenoma_or_serrated: { minYears: 3, maxYears: 3 },
  high_adenoma_count: { minYears: 3, maxYears: 3 },
  high_serrated_count: { minYears: 3, maxYears: 3 },
  moderate_adenoma_count: { minYears: 3, maxYears: 5 },
  moderate_serrated_count: { minYears: 3, maxYears: 5 },
  non_diminutive_hyperplastic_polyp: { minYears: 3, maxYears: 5 },
  low_adenoma_count: { minYears: 7, maxYears: 10 },
  low_serrated_count: { minYears: 5, maxYears: 10 },
  no_polyps_or_diminutive_hyperplastic: { minYears: 10, maxYears: 10 },
} as const;

const CATEGORY_REASONS: Readonly<Record<SurveillanceCategory, string>> = {
  piecemeal_large_polyp_resection:
    'Piecemeal resection of a polyp ≥ 20mm — early post-resection surveillance exam',
  more_than_10_adenomas: 'More than 10 adenomas in one exam',
  high_risk_adenoma_or_serrated:
    'An adenoma ≥ 10mm, villous histology, high-grade dysplasia, or a sessile serrated lesion ≥ 10mm / with dysplasia / traditional serrated adenoma',
  high_adenoma_count: '5-10 tubular adenomas, all < 10mm',
  high_serrated_count: '5-10 sessile serrated lesions, all < 10mm',
  moderate_adenoma_count: '3-4 tubular adenomas, all < 10mm',
  moderate_serrated_count: '3-4 sessile serrated lesions, all < 10mm',
  non_diminutive_hyperplastic_polyp:
    'A hyperplastic polyp ≥ 10mm, or outside the rectum/sigmoid, with no higher-risk finding on the same report',
  low_adenoma_count: '1-2 tubular adenomas, both < 10mm',
  low_serrated_count: '1-2 sessile serrated lesions, both < 10mm, no dysplasia',
  no_polyps_or_diminutive_hyperplastic:
    'No polyps, or only diminutive hyperplastic polyps in the rectum/sigmoid — average-risk screening interval',
};

/** One polyp finding as this engine needs it — a narrower view than the full DB record. */
export interface SurveillancePolypInput {
  readonly sizeMm: number;
  readonly histology: HistologyType;
  readonly dysplasiaGrade: DysplasiaGrade | null;
  readonly location: 'rectum' | 'sigmoid_colon' | 'other';
  /** Piecemeal resection is recorded at the report level in this codebase's schema (see
   * `reportService.ts`), not per-polyp, but the engine takes it as an optional per-polyp flag so a
   * caller can pass it through however it is modeled without this module depending on the DB shape. */
  readonly piecemealResectionOfLargePolyp: boolean;
}

function isDiminutiveRectosigmoidHyperplastic(polyp: SurveillancePolypInput): boolean {
  return (
    polyp.histology === 'hyperplastic_polyp' &&
    polyp.sizeMm < 10 &&
    (polyp.location === 'rectum' || polyp.location === 'sigmoid_colon')
  );
}

function isHighRiskFinding(polyp: SurveillancePolypInput): boolean {
  if (polyp.piecemealResectionOfLargePolyp) return false; // handled as its own top category
  if (isAdenomaHistology(polyp.histology)) {
    // Any adenoma carrying villous architecture — `villous_adenoma` (pure) or
    // `tubulovillous_adenoma` (mixed, villous by definition alongside tubular) — is high-risk
    // regardless of size or dysplasia grade, per this module's own header rule ("Any adenoma with
    // villous histology or high-grade dysplasia — 3 years"); `tubular_adenoma` alone is not. Found
    // and fixed by this module's own test suite: an earlier version's comment here incorrectly
    // reasoned that being counted by `isAdenomaHistology` was sufficient, but that function only
    // decides whether a polyp counts as an adenoma *at all* (for the count-based categories below)
    // — it says nothing about risk level, so a small villous adenoma with no dysplasia fell through
    // to `low_adenoma_count` instead of the high-risk category until this explicit check was added.
    return (
      polyp.sizeMm >= 10 ||
      polyp.dysplasiaGrade === 'high_grade' ||
      polyp.histology === 'villous_adenoma' ||
      polyp.histology === 'tubulovillous_adenoma'
    );
  }
  if (isSerratedHistology(polyp.histology)) {
    return (
      polyp.sizeMm >= 10 ||
      polyp.dysplasiaGrade !== null ||
      polyp.histology === 'traditional_serrated_adenoma'
    );
  }
  return false;
}

/**
 * Evaluate every polyp on a report and return the single most conservative matching category — see
 * this module's header. UNREVIEWED, per that same header: build and test the *logic*, do not treat
 * the returned category's interval as validated clinical guidance.
 */
export function computeSurveillanceCategory(
  polyps: readonly SurveillancePolypInput[],
): SurveillanceCategory {
  if (polyps.some((p) => p.piecemealResectionOfLargePolyp)) {
    return 'piecemeal_large_polyp_resection';
  }

  const adenomas = polyps.filter((p) => isAdenomaHistology(p.histology));
  const serrated = polyps.filter((p) => isSerratedHistology(p.histology));

  if (adenomas.length > 10) return 'more_than_10_adenomas';
  if (polyps.some(isHighRiskFinding)) return 'high_risk_adenoma_or_serrated';
  if (adenomas.length >= 5) return 'high_adenoma_count';
  if (serrated.length >= 5) return 'high_serrated_count';
  if (adenomas.length >= 3) return 'moderate_adenoma_count';
  if (serrated.length >= 3) return 'moderate_serrated_count';

  // A hyperplastic polyp that is *not* diminutive-and-rectosigmoid (≥10mm, or outside the
  // rectum/sigmoid) is a real finding at "moderate" priority, checked here — before the "low"
  // adenoma/serrated-count checks below — so it can't be silently outranked by a less-conservative
  // low-count finding on the same report. `emr-po`'s E8 review flagged an earlier version of this
  // function as falling through all the way to `no_polyps_or_diminutive_hyperplastic` (a 10-year
  // average-risk interval) for exactly this case — worse than the flat-3-year mapping E8 was
  // actually reviewing, since `isSerratedHistology` deliberately excludes `hyperplastic_polyp`
  // (correctly, for ADR-counting purposes) so this case never reached `isHighRiskFinding`'s serrated
  // branch at all. Per E8's correction: 3-5 years, the same range as a moderate serrated finding —
  // not USMSTF's most-aggressive bucket (reserved for a true sessile serrated lesion/TSA ≥10mm or
  // with dysplasia), and not the average-risk 10-year interval either.
  if (
    polyps.some(
      (p) => p.histology === 'hyperplastic_polyp' && !isDiminutiveRectosigmoidHyperplastic(p),
    )
  ) {
    return 'non_diminutive_hyperplastic_polyp';
  }

  if (adenomas.length >= 1) return 'low_adenoma_count';
  if (serrated.length >= 1) return 'low_serrated_count';

  // Reached by a diminutive rectosigmoid hyperplastic polyp (correctly average-risk), by no polyps
  // at all, OR — a real, separate, NOT-yet-fixed gap, out of scope for E8's fix above and named here
  // rather than silently left implicit — by an `adenocarcinoma`/`normal_mucosa`/`other` histology,
  // which has no matching category in this table and should almost certainly not resolve to a
  // 10-year average-risk interval, especially `adenocarcinoma`. `destructionCharges.ts`'s own
  // refuse-rather-than-guess precedent is the right model, but this function's return type has no
  // "refused"/unrepresentable variant to actually do that with — needs its own fix, tracked
  // separately, not folded into E8's HP-mapping correction.
  return 'no_polyps_or_diminutive_hyperplastic';
}

export interface SurveillanceRecommendation {
  readonly category: SurveillanceCategory;
  readonly interval: SurveillanceIntervalRange;
  readonly reason: string;
}

/** UNREVIEWED — see this module's header. */
export function recommendSurveillanceInterval(
  polyps: readonly SurveillancePolypInput[],
): SurveillanceRecommendation {
  const category = computeSurveillanceCategory(polyps);
  return { category, interval: CATEGORY_INTERVALS[category], reason: CATEGORY_REASONS[category] };
}
